import { Link, createFileRoute, notFound } from '@tanstack/react-router'
import { flag } from '@/auth/me'
import { lazy, Suspense } from 'react'
import { LuxEstimator, LuxReference, StripCalculator } from '@/features/tools/other-tools'
import { RoiTool } from '@/features/tools/roi-tool'

// The MagNiko catalogue and screens load only when the configurator is opened.
const MagnikoConfigurator = lazy(() =>
  import('@/features/magniko/configurator').then((m) => ({ default: m.MagnikoConfigurator })),
)

const TITLES: Record<string, { flag: string; title: string }> = {
  roi: { flag: 'tool_roi', title: 'Energy Savings & ROI' },
  lux: { flag: 'tool_lux_estimator', title: 'Quick Lux Estimator' },
  reference: { flag: 'tool_lux_reference', title: 'Lux Level Reference' },
  strip: { flag: 'tool_strip_calculator', title: 'Strip Light Calculator' },
  magniko: { flag: 'tool_magniko', title: 'MagNiko Configurator' },
}

export const Route = createFileRoute('/_app/tools/$tool')({
  validateSearch: (s: Record<string, unknown>): { relux?: string } => ({
    relux: typeof s.relux === 'string' ? s.relux : undefined,
  }),
  beforeLoad: ({ params, context }) => {
    const t = TITLES[params.tool]
    if (!t || !flag(context.me, t.flag)) throw notFound()
  },
  component: Tool,
})

function Tool() {
  const { tool } = Route.useParams()
  const { relux } = Route.useSearch()
  const { me } = Route.useRouteContext()
  return (
    <div
      className={`mx-auto flex flex-col gap-6 ${tool === 'magniko' ? 'max-w-7xl' : 'max-w-5xl'}`}
    >
      <Link
        to="/tools"
        className="font-semibold text-brand-text underline underline-offset-4 print:hidden"
      >
        All tools
      </Link>
      <h1 className="text-3xl uppercase">{TITLES[tool]!.title}</h1>
      {tool === 'roi' && <RoiTool reluxId={relux} />}
      {tool === 'lux' && <LuxEstimator />}
      {tool === 'reference' && <LuxReference me={me} />}
      {tool === 'strip' && <StripCalculator />}
      {tool === 'magniko' && (
        <Suspense fallback={<p className="text-fg-muted">Loading the configurator...</p>}>
          <MagnikoConfigurator userId={me.real_user_id} />
        </Suspense>
      )}
    </div>
  )
}
